import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy, Flame, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';

const CELEBRATION_CONFIG = {
  personal_record: {
    icon: Trophy,
    color: '#FFD700',
    confettiColors: ['#FFD700', '#FFA500', '#FF6B35', '#FFFFFF', '#D4FF00'],
    label_fr: 'Record Personnel',
    label_en: 'Personal Record',
  },
  milestone: {
    icon: Flame,
    color: '#FF6B35',
    confettiColors: ['#FF6B35', '#FF4500', '#FFD700', '#FFFFFF', '#D4FF00'],
    label_fr: 'Palier Atteint',
    label_en: 'Milestone Reached',
  },
  consistency_7d: {
    icon: BarChart3,
    color: '#10B981',
    confettiColors: ['#10B981', '#34D399', '#6EE7B7', '#FFFFFF', '#D4FF00'],
    label_fr: 'Constance',
    label_en: 'Consistency',
  },
};

const fireGoldenConfetti = (type) => {
  const config = CELEBRATION_CONFIG[type] || CELEBRATION_CONFIG.personal_record;

  // Burst 1 - main
  confetti({
    particleCount: 150,
    spread: 100,
    origin: { y: 0.6 },
    colors: config.confettiColors,
    startVelocity: 35,
  });

  // Burst 2 - delayed
  setTimeout(() => {
    confetti({
      particleCount: 100,
      spread: 120,
      origin: { y: 0.5 },
      colors: config.confettiColors,
      startVelocity: 45,
      decay: 0.92,
    });
  }, 200);

  // Star burst for records
  if (type === 'personal_record') {
    setTimeout(() => {
      confetti({
        spread: 360,
        ticks: 100,
        gravity: 0,
        decay: 0.94,
        startVelocity: 20,
        colors: ['#FFD700', '#D4FF00', '#FFFFFF'],
        particleCount: 60,
        scalar: 1.5,
        shapes: ['star'],
      });
    }, 400);

    // Side bursts
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.65 },
        colors: config.confettiColors,
      });
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.65 },
        colors: config.confettiColors,
      });
    }, 300);
  }
};

export const RecordCelebration = ({ celebration, onClose, language = 'fr' }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (celebration) {
      fireGoldenConfetti(celebration.type);
    }
  }, [celebration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!celebration) return null;

  const config = CELEBRATION_CONFIG[celebration.type] || CELEBRATION_CONFIG.personal_record;
  const IconComponent = config.icon;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.88)' }}
          onClick={handleClose}
          data-testid="record-celebration-overlay"
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 15, stiffness: 300 }}
            className="relative max-w-md w-full rounded-2xl p-8 text-center"
            style={{
              background: `linear-gradient(135deg, ${config.color}15 0%, #18181B 50%, ${config.color}08 100%)`,
              border: `3px solid ${config.color}`,
              boxShadow: `0 0 60px ${config.color}40, 0 0 120px ${config.color}20`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="celebration-close-btn"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: 'spring', damping: 10 }}
              className="mb-5"
            >
              <div
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${config.color}30, ${config.color}10)`,
                  border: `2px solid ${config.color}60`,
                }}
              >
                <IconComponent className="w-10 h-10" style={{ color: config.color }} />
              </div>
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <p
                className="text-xs font-bold uppercase tracking-widest mb-2"
                style={{ color: config.color }}
              >
                {language === 'fr' ? config.label_fr : config.label_en}
              </p>
              <h2
                className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight mb-3"
                style={{ color: config.color }}
                data-testid="celebration-title"
              >
                {celebration.title}
              </h2>
            </motion.div>

            {/* Message */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-muted-foreground text-sm leading-relaxed mb-6"
              data-testid="celebration-message"
            >
              {celebration.message}
            </motion.p>

            {/* Action */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <Button
                onClick={handleClose}
                className="px-8 py-3 font-heading uppercase tracking-wider text-black"
                style={{ backgroundColor: config.color }}
                data-testid="celebration-dismiss-btn"
              >
                {celebration.type === 'personal_record'
                  ? (language === 'fr' ? 'All in !' : 'All in!')
                  : celebration.type === 'milestone'
                  ? (language === 'fr' ? 'On continue !' : 'Keep going!')
                  : (language === 'fr' ? 'Merci Coach !' : 'Thanks Coach!')
                }
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecordCelebration;
